/**
 * React Component Registry
 * 
 * This module provides a central registry for React components that can be
 * mounted from Rails views via Stimulus controllers. It enables seamless
 * integration between Rails/Phlex server-rendered HTML and React components.
 * 
 * Usage:
 * 1. Register components: registry.register('UserManagement', UserManagement)
 * 2. Mount from Rails: <div data-react-component="UserManagement" data-react-props='{"users": [...]}'>
 * 3. The Stimulus controller handles mounting/unmounting automatically
 */

import { createElement, type ComponentType } from 'react';
import { createRoot, hydrateRoot, type Root } from 'react-dom/client';
import { Providers } from './providers';
import { TranslationProvider, type TranslationObject } from './hooks/useTranslation';
import { createLogger } from '@/utils/logger';

const log = createLogger('ReactRegistry');

// Type for any React component (functional or class)
type ReactComponent<P = Record<string, unknown>> = ComponentType<P>;

// Registry storage for components and active roots
const components = new Map<string, ReactComponent>();
const activeRoots = new Map<HTMLElement, Root>();

/**
 * Register a React component with the registry
 * @param name - Unique identifier for the component (e.g., 'UserManagement')
 * @param component - The React component to register
 */
export function register<P = Record<string, unknown>>(
  name: string,
  component: ReactComponent<P>
): void {
  if (components.has(name)) {
    log.warn(`Component "${name}" is already registered. Overwriting.`);
  }
  components.set(name, component as ReactComponent);
  
  if (import.meta.env.DEV) {
    console.log(`[ReactRegistry] Registered component: ${name}`);
  }
}

/**
 * Get a registered component by name
 * @param name - The component name to look up
 * @returns The registered component or undefined
 */
export function get(name: string): ReactComponent | undefined {
  return components.get(name);
}

/**
 * Check if a component is registered
 * @param name - The component name to check
 */
export function has(name: string): boolean {
  return components.has(name);
}

/**
 * Get all registered component names
 */
export function getRegisteredNames(): string[] {
  return Array.from(components.keys());
}

/**
 * Mount a React component into a DOM element
 * @param element - The DOM element to mount the component into
 * @param componentName - The registered component name
 * @param props - Props to pass to the component
 * @returns true if mounted successfully, false otherwise
 */
export function mount(
  element: HTMLElement,
  componentName: string,
  props: Record<string, unknown> = {},
  options: { hydrate?: boolean } = {}
): boolean {
  const Component = components.get(componentName);

  if (!Component) {
    log.error(
      `Component "${componentName}" not found. ` +
      `Available components: ${getRegisteredNames().join(', ') || 'none'}`
    );
    return false;
  }

  // Clean up any existing root on this element
  unmount(element);

  try {
    // Extract translations from props (if present)
    const { translations, ...componentProps } = props;
    const translationData = (translations || {}) as TranslationObject;

    const tree = createElement(Providers, null,
      createElement(TranslationProvider, { translations: translationData },
        createElement(Component, componentProps)
      )
    );

    // Hydration preserves the server-rendered DOM (and any running CSS
    // animations) instead of swapping it out — only safe when the caller's
    // server HTML matches React's first render (see Components::Skeletons::*).
    const root = options.hydrate
      ? hydrateRoot(element, tree)
      : (() => {
          const r = createRoot(element);
          r.render(tree);
          return r;
        })();
    activeRoots.set(element, root);

    if (import.meta.env.DEV) {
      console.log(`[ReactRegistry] Mounted "${componentName}" with props:`, componentProps);
    }

    return true;
  } catch (error) {
    log.error(`Failed to mount "${componentName}":`, error);
    return false;
  }
}

/**
 * Unmount a React component from a DOM element
 * @param element - The DOM element to unmount from
 * @returns true if unmounted successfully, false if no component was mounted
 */
export function unmount(element: HTMLElement): boolean {
  const root = activeRoots.get(element);
  
  if (root) {
    try {
      root.unmount();
      activeRoots.delete(element);
      
      if (import.meta.env.DEV) {
        console.log('[ReactRegistry] Unmounted component');
      }
      
      return true;
    } catch (error) {
      log.error('Failed to unmount component:', error);
      return false;
    }
  }
  
  return false;
}

/**
 * Unmount all active React components
 * Useful for cleanup during page transitions
 */
export function unmountAll(): void {
  activeRoots.forEach((root, _element) => {
    try {
      root.unmount();
    } catch (error) {
      log.error('Failed to unmount component during cleanup:', error);
    }
  });
  activeRoots.clear();
  
  if (import.meta.env.DEV) {
    console.log('[ReactRegistry] Unmounted all components');
  }
}

/**
 * Get count of currently mounted components
 */
export function getMountedCount(): number {
  return activeRoots.size;
}

// Create the registry object for convenient access
const registry = {
  register,
  get,
  has,
  getRegisteredNames,
  mount,
  unmount,
  unmountAll,
  getMountedCount,
};

// Export as default for convenient imports
export default registry;

// Also expose on window for debugging in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as { __REACT_REGISTRY__: typeof registry }).__REACT_REGISTRY__ = registry;
}

